import { AIServiceError } from '../../QnAWidget/services';

export class ErrorMessageBuilder {
  /**
   * Converts technical error messages into user-friendly messages
   * Logs full error details to console for developers
   */
  public static buildUserFriendlyMessage(error: unknown): string {
    if (!(error instanceof AIServiceError)) {
      return 'Failed to send message. Please try again.';
    }

    // Log full error details for developers (visible in browser console)
    console.error('[QnA Error]', {
      code: error.code,
      message: error.message,
      retryable: error.retryable,
      timestamp: new Date().toISOString(),
    });

    switch (error.code) {
      case 'CONFIG_ERROR':
        return 'AI service is not properly configured.';
      case 'RATE_LIMIT':
        return 'Too many requests. Please wait a moment and try again.';
      case 'TIMEOUT':
        return 'Request timeout. Please try again.';
      case 'API_ERROR':
        return this.parseAPIError(error.message);
      case 'NETWORK_ERROR':
        return 'Network connection problem. Please check your internet connection.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Parses API error messages and returns user-friendly versions
   * @private
   */
  private static parseAPIError(message: string): string {
    // Check for common API error patterns
    if (message.includes('401') || message.toLowerCase().includes('api key')) {
      return 'AI service authentication error. Please contact support.';
    }

    if (message.includes('429') || message.toLowerCase().includes('rate limit')) {
      return 'Too many requests to AI service. Please wait and try again.';
    }

    if (message.includes('403') || message.toLowerCase().includes('forbidden')) {
      return 'Access denied to AI service. Please contact support.';
    }

    if (message.includes('404') || message.toLowerCase().includes('not found')) {
      return 'AI service endpoint not found. Please contact support.';
    }

    if (message.includes('500') || message.includes('503') || message.toLowerCase().includes('server error')) {
      return 'AI service is temporarily unavailable. Please try again later.';
    }

    // Generic API error message
    return 'Unable to connect to AI service. Please try again or contact support.';
  }
}
